import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const LATEST_DESKTOP_RELEASE_URL =
  'https://downloads.afternoon.co.uk/desktop/stable/latest.json';

export const DESKTOP_PLATFORMS = ['darwin/arm64', 'win32/x64'] as const;

export type DesktopPlatform = (typeof DESKTOP_PLATFORMS)[number];

export const DESKTOP_PLATFORM_LABELS: Record<DesktopPlatform, string> = {
  'darwin/arm64': 'Mac (Apple silicon)',
  'win32/x64': 'Windows',
};

const installerSchema = z.object({
  url: z.string().url(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  size: z.number().int().positive(),
});

const latestDesktopReleaseSchema = z.object({
  version: z.string().min(1),
  installers: z.record(z.string(), installerSchema),
});

export type DesktopInstaller = z.infer<typeof installerSchema>;

type LatestDesktopRelease = z.infer<typeof latestDesktopReleaseSchema>;

export function useLatestDesktopRelease() {
  return useQuery({
    queryKey: ['desktop-release', 'latest'],
    queryFn: fetchLatestDesktopRelease,
    staleTime: 5 * 60 * 1000,
  });
}

async function fetchLatestDesktopRelease(): Promise<LatestDesktopRelease> {
  const response = await fetch(LATEST_DESKTOP_RELEASE_URL);
  if (!response.ok) {
    throw new Error(`The release manifest returned HTTP ${response.status}`);
  }
  return latestDesktopReleaseSchema.parse(await response.json());
}

type NavigatorPlatformInfo = {
  userAgent: string;
  maxTouchPoints: number;
  userAgentData?: { platform: string };
};

/**
 * The desktop platform whose installer this browser can run, or undefined when there is none.
 * Browsers do not reliably expose the CPU architecture, so every Mac is offered the Apple silicon
 * build and the label says so.
 */
export function detectDesktopPlatform(
  navigatorInfo: NavigatorPlatformInfo,
): DesktopPlatform | undefined {
  const platform = navigatorInfo.userAgentData?.platform;
  if (platform === 'macOS') return 'darwin/arm64';
  if (platform === 'Windows') return 'win32/x64';
  if (platform) return undefined;

  // iPadOS Safari reports a Macintosh user agent; touch support is what tells it apart.
  if (/Macintosh/.test(navigatorInfo.userAgent) && navigatorInfo.maxTouchPoints <= 1) {
    return 'darwin/arm64';
  }
  if (/Windows NT/.test(navigatorInfo.userAgent)) return 'win32/x64';
  return undefined;
}

export function installerFileName(installer: DesktopInstaller) {
  return decodeURIComponent(new URL(installer.url).pathname.split('/').pop() ?? '');
}
